import { skipToken } from '@reduxjs/toolkit/query';
import type { GetConnectionSyncRunProgressApiArg } from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';

const POLLING_INTERVAL_MS = 3000;

export interface UseSyncRunProgressParams {
  providerId: GetConnectionSyncRunProgressApiArg['provider'] | undefined;
  connectionId: string | undefined;
  /** Poll only while this is true (e.g. while the surface is visible). */
  enabled: boolean;
}

/**
 * Polls the connection's active fan-out sync_runs and derives display counters.
 * `total` (= outstanding + ok + error) grows as a run discovers units; `completed`
 * (= ok + error) only ever rises — render counts, not a percentage, so progress
 * never appears to move backwards.
 *
 * A webhook/operations run can be in flight alongside the connection's single
 * active initialize/synchronize run; the user-triggered full-sync run is the
 * headline, so it wins when both are active. Legacy providers have no sync_run
 * rows — `showProgress` stays false and callers keep their generic copy.
 */
export function useSyncRunProgress({
  providerId,
  connectionId,
  enabled,
}: UseSyncRunProgressParams) {
  const queryActive = Boolean(providerId && connectionId && enabled);
  const { data } = ticApi.useGetConnectionSyncRunProgressQuery(
    queryActive ? { provider: providerId!, connectionId: connectionId! } : skipToken,
    { pollingInterval: POLLING_INTERVAL_MS }
  );

  const runs = data?.data ?? [];
  const run =
    runs.find(r => r.preset === 'initialize' || r.preset === 'synchronize') ?? runs[0] ?? null;
  const total = run ? run.outstanding + run.ok_count + run.error_count : 0;
  const completed = run ? run.ok_count + run.error_count : 0;

  const hasActiveInitialize = runs.some(r => r.preset === 'initialize');
  const hasActiveSynchronize = runs.some(r => r.preset === 'synchronize');

  return {
    run,
    showProgress: run !== null && total > 0,
    completed,
    total,
    /**
     * An `initialize` full-sync run is currently running for this connection. The setup
     * wizard runs initialize and synchronize as distinct phases, so resume logic must be
     * able to tell which phase is in flight (a resumed initialize still has a synchronize
     * follow-up to run; a resumed synchronize is terminal).
     */
    hasActiveInitialize,
    /** A `synchronize` full-sync run is currently running for this connection. */
    hasActiveSynchronize,
    /**
     * A user-triggered full-sync run (initialize/synchronize) is currently running
     * for this connection. Legacy providers have no sync_run rows, so this stays false.
     */
    hasActiveSyncRun: hasActiveInitialize || hasActiveSynchronize,
    /**
     * The progress query has returned at least once, so `run`/`hasActiveSyncRun` are
     * trustworthy. When the query is disabled (legacy provider or missing ids) there is
     * nothing to wait for, so it is treated as resolved immediately.
     */
    isResolved: !queryActive || data !== undefined,
  };
}
